import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import {
  useDeleteFormTemplateMutation,
  useFetchFormTemplateMutation,
} from "../../../features/admin/formTemplate/formTemplateApi";
import { toast } from "react-hot-toast";
import {
  FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX,
  FiUploadCloud, FiList, FiChevronDown,
} from "react-icons/fi";
import { HiX } from "react-icons/hi";

const FORM_TYPES = [
  { id: 1,  name: "Payment" },      { id: 2,  name: "Warranty" },
  { id: 3,  name: "KYC" },          { id: 4,  name: "Login" },
  { id: 5,  name: "Registration" }, { id: 6,  name: "Profile" },
  { id: 7,  name: "Panel-AddUser" },{ id: 8,  name: "App-AddUser" },
  { id: 9,  name: "Enquiry" },      { id: 10, name: "WheelCampaign" },
  { id: 11, name: "App-Feedback" }, { id: 12, name: "Report-Qr" },
];

const TYPE_META = {
  text:     { label: "Text",     color: "bg-blue-50 text-blue-700 border-blue-200" },
  number:   { label: "Number",   color: "bg-violet-50 text-violet-700 border-violet-200" },
  date:     { label: "Date",     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  file:     { label: "File",     color: "bg-amber-50 text-amber-700 border-amber-200" },
  camera:   { label: "Camera",   color: "bg-pink-50 text-pink-700 border-pink-200" },
  checkbox: { label: "Checkbox", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  select:   { label: "Select",   color: "bg-orange-50 text-orange-700 border-orange-200" },
  textarea: { label: "Textarea", color: "bg-teal-50 text-teal-700 border-teal-200" },
};

const FORM_TYPE_CHIP_COLORS = [
  "bg-blue-50 text-blue-700 border-blue-200",
  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "bg-violet-50 text-violet-700 border-violet-200",
  "bg-amber-50 text-amber-700 border-amber-200",
  "bg-pink-50 text-pink-700 border-pink-200",
  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "bg-orange-50 text-orange-700 border-orange-200",
  "bg-teal-50 text-teal-700 border-teal-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-cyan-50 text-cyan-700 border-cyan-200",
  "bg-lime-50 text-lime-700 border-lime-200",
  "bg-sky-50 text-sky-700 border-sky-200",
];

const getFormTypeName  = (id) => FORM_TYPES.find(t => t.id === Number(id))?.name || `Type ${id}`;
const getFormTypeColor = (id) => FORM_TYPE_CHIP_COLORS[(Number(id) - 1) % FORM_TYPE_CHIP_COLORS.length];

// ─── Field preview card inside the modal ────────────────────────────────────
export const FieldPreviewCard = ({ field }) => {
  const meta = TYPE_META[field.type] || { label: field.type, color: "bg-gray-100 text-gray-600 border-gray-200" };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${meta.color}`}>
          {meta.label}
        </span>
        {field.required && (
          <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
            Required
          </span>
        )}
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800">{field.label}</p>
        <p className="text-xs text-gray-400 font-mono mt-0.5">{field.name}</p>
      </div>
      {field.type === "select" ? (
        <div className="relative">
          <select className="block w-full pl-3 pr-9 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 appearance-none cursor-not-allowed" disabled>
            <option>Select {field.label}…</option>
            {(field.options || []).map(o => <option key={o}>{o}</option>)}
          </select>
          <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
        </div>
      ) : field.type === "checkbox" ? (
        <div className="flex items-center gap-2">
          <input type="checkbox" disabled className="rounded border-gray-300" />
          <span className="text-sm text-gray-400">{field.label}</span>
        </div>
      ) : field.type === "textarea" ? (
        <textarea className="block w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 resize-none" rows={2} disabled placeholder={field.label} />
      ) : field.type === "file" || field.type === "camera" ? (
        <div className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl px-3 py-4 bg-gray-50">
          <FiUploadCloud className="h-5 w-5 text-gray-300" />
          <span className="text-xs text-gray-400">Click to upload</span>
        </div>
      ) : (
        <input
          type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
          disabled
          placeholder={field.label}
          className="block w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 cursor-not-allowed"
        />
      )}
    </div>
  );
};

// ─── Custom DataTable styles ──────────────────────────────────────────────────
const customTableStyles = {
  headRow:   { style: { backgroundColor: "#f8fafc", borderBottomColor: "#e2e8f0", borderBottomWidth: "1px" } },
  headCells: { style: { fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", paddingLeft: "16px", paddingRight: "16px" } },
  cells:     { style: { fontSize: "13px", color: "#374151", paddingLeft: "16px", paddingRight: "16px", paddingTop: "8px", paddingBottom: "8px" } },
  rows:      { style: { borderBottomColor: "#f1f5f9" }, highlightOnHoverStyle: { backgroundColor: "#f8fafc", transitionDuration: "0.1s" } },
};

// ─────────────────────────────────────────────────────────────────────────────

export default function FormTemplate() {
  const navigate = useNavigate();
  const [open, setOpen]               = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [tableDataItems, setTableDataItems] = useState([]);
  const [nameFilter, setNameFilter]   = useState("");
  const [typeFilter, setTypeFilter]   = useState("all");

  const [fetchFormTemplates, { data: formTemplateData, error: formTemplateError }] =
    useFetchFormTemplateMutation();
  const [deleteFormTemplate, { data: deleteFormTemplateData, error: deleteformTemplateError }] =
    useDeleteFormTemplateMutation();

  useEffect(() => {
    fetchFormTemplates(sessionStorage.getItem("access_token"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMemo(() => {
    if (formTemplateData) setTableDataItems(formTemplateData.body);
    if (formTemplateError) console.log("error", formTemplateError);
  }, [formTemplateData, formTemplateError]);

  useMemo(() => {
    if (deleteFormTemplateData) {
      toast.dismiss();
      fetchFormTemplates(sessionStorage.getItem("access_token"));
    }
    if (deleteformTemplateError) {
      toast.dismiss();
      if (deleteformTemplateError.status === 409)
        toast.error(deleteformTemplateError.data.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteFormTemplateData, deleteformTemplateError]);

  const handleDeleteWrapper = (r) => {
    toast(
      (t) => (
        <span>
          <p className="font-semibold text-gray-800">Delete "{r.template_name}"?</p>
          <p className="text-xs text-gray-500 mt-0.5">This cannot be undone.</p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium"
              onClick={() => {
                deleteFormTemplate({ token: sessionStorage.getItem("access_token"), id: r.id });
                toast.dismiss(t.id);
              }}
            >Delete</button>
            <button
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              onClick={() => toast.dismiss(t.id)}
            >Cancel</button>
          </div>
        </span>
      ),
      { id: "deleteForm", duration: Infinity }
    );
  };

  // Type pill options — only types present in loaded data
  const availableTypes = useMemo(() => {
    const ids = new Set((tableDataItems || []).map(r => Number(r.form_type)));
    return FORM_TYPES.filter(t => ids.has(t.id));
  }, [tableDataItems]);

  const filteredData = useMemo(() => {
    return (tableDataItems || []).filter(row => {
      const matchName = !nameFilter || row.template_name?.toLowerCase().includes(nameFilter.toLowerCase());
      const matchType = typeFilter === "all" || String(row.form_type) === String(typeFilter);
      return matchName && matchType;
    });
  }, [tableDataItems, nameFilter, typeFilter]);

  const columns = [
    {
      name: "#",
      selector: (_, idx) => idx + 1,
      cell:     (_, idx) => <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>,
      width: "60px",
    },
    {
      name: "Template Name",
      selector: row => row.template_name,
      sortable: true,
      cell: row => (
        <div>
          <p className="font-semibold text-gray-800 text-sm">{row.template_name}</p>
          <p className="text-xs text-gray-400 font-mono mt-0.5">ID #{row.id}</p>
        </div>
      ),
      grow: 2,
    },
    {
      name: "Form Type",
      cell: row => (
        <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${getFormTypeColor(row.form_type)}`}>
          {getFormTypeName(row.form_type)}
        </span>
      ),
    },
    {
      name: "Industry",
      selector: row => row.industry_name,
      sortable: true,
      cell: row => <span className="text-sm text-gray-600">{row.industry_name || "—"}</span>,
    },
    {
      name: "Fields",
      center: true,
      width: "80px",
      cell: row => (
        <span className="inline-flex items-center justify-center h-6 min-w-[28px] px-1.5 rounded-full bg-gray-100 text-xs font-bold text-gray-700">
          {Object.keys(row.template || {}).length}
        </span>
      ),
    },
    {
      name: "Language",
      selector: row => row.language,
      cell: row => <span className="text-xs text-gray-500 capitalize">{row.language || "—"}</span>,
      width: "100px",
    },
    {
      name: "Created",
      selector: row => row.created_at,
      sortable: true,
      cell: row => (
        <div>
          <p className="text-xs text-gray-600">{new Date(row.created_at).toLocaleDateString()}</p>
          <p className="text-xs text-gray-400">{row.created_by_name}</p>
        </div>
      ),
      width: "120px",
    },
    {
      name: "Actions",
      minWidth: "200px",
      ignoreRowClick: true,
      cell: row => (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => { setTemplateData(row); setOpen(true); }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium transition-colors"
          >
            <FiEye className="h-3.5 w-3.5" /> View
          </button>
          <button
            onClick={() => navigate("/addForm", { state: row })}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-xs font-medium transition-colors"
          >
            <FiEdit2 className="h-3.5 w-3.5" /> Edit
          </button>
          <button
            onClick={() => handleDeleteWrapper(row)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-colors"
          >
            <FiTrash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <div className="card p-0 rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h4 className="text-xl font-bold text-gray-900 mb-0">Forms Master</h4>
            <p className="text-sm text-gray-500 mt-0.5">Manage dynamic form templates across all industries.</p>
          </div>
          <button
            onClick={() => navigate("/addForm")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-all"
          >
            <FiPlus className="h-4 w-4" /> Create Form
          </button>
        </div>

        {/* Filter bar */}
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative w-full sm:w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name…"
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                className="block w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 items-center">
              <button
                onClick={() => setTypeFilter("all")}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${typeFilter === "all" ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              >
                All
              </button>
              {availableTypes.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTypeFilter(String(t.id))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${typeFilter === String(t.id) ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
                >
                  {t.name}
                </button>
              ))}
              {(nameFilter || typeFilter !== "all") && (
                <button
                  onClick={() => { setNameFilter(""); setTypeFilter("all"); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 transition-all"
                >
                  <FiX className="h-3 w-3" /> Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="px-2">
          <DataTable
            data={filteredData}
            columns={columns}
            customStyles={customTableStyles}
            pagination
            highlightOnHover
            pointerOnHover
            noDataComponent={
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <FiList className="h-12 w-12 text-gray-200" />
                <p className="text-sm font-semibold text-gray-400">No forms found</p>
                <p className="text-xs text-gray-300">Try adjusting your filters or create a new form.</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Preview Modal */}
      {open && templateData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{templateData.template_name}</h3>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${getFormTypeColor(templateData.form_type)}`}>
                    {getFormTypeName(templateData.form_type)}
                  </span>
                  <span className="text-xs text-gray-400">{templateData.industry_name}</span>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-400 capitalize">{templateData.language}</span>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-400">{Object.keys(templateData.template || {}).length} fields</span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors flex-shrink-0 ml-4">
                <HiX className="h-4 w-4" />
              </button>
            </div>
            {/* Modal body */}
            <div className="overflow-auto flex-1 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.values(templateData.template).map((field, idx) => (
                  <FieldPreviewCard key={idx} field={field} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
